import { motion } from 'motion/react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { 
  Home as HomeIcon, 
  Users, 
  TrendingUp, 
  Search,
  CheckCircle,
  Scale,
  Building,
  UserCheck,
  ArrowRightLeft,
  ArrowRight,
  Sparkles
} from 'lucide-react';

interface ServicesProps {
  onNavigateToProperties: () => void;
  onNavigateToHub: () => void;
}

export function ModernServices({ onNavigateToProperties, onNavigateToHub }: ServicesProps) {
  const coreOfferings = [
    {
      icon: Scale,
      title: 'Conveyancing',
      description: 'We have partnered with the esteemed law firm Phello Attorneys, which provides us with accredited conveyancers.',
      gradient: 'from-orange-500/20 to-yellow-500/20'
    },
    {
      icon: Building,
      title: 'Property Development',
      description: 'Whether your property needs some renovations, building or even an investment boost, we are here to make your property journey seamless.',
      gradient: 'from-blue-500/20 to-cyan-500/20'
    },
    {
      icon: UserCheck,
      title: 'Estate Agents',
      description: 'Our estate agents go above and beyond to get results faster than the industry average. Come and see the difference.',
      gradient: 'from-purple-500/20 to-pink-500/20'
    },
    {
      icon: ArrowRightLeft,
      title: 'Bridging Finance',
      description: 'Let us help you to bridge that gap and say goodbye to your cash flow problems.',
      gradient: 'from-green-500/20 to-emerald-500/20'
    },
    {
      icon: Users,
      title: 'Contractors',
      description: 'Access our network of trusted contractors for all your property maintenance and renovation needs.',
      gradient: 'from-red-500/20 to-rose-500/20'
    }
  ];

  const mainServices = [
    {
      id: 'sales',
      icon: HomeIcon,
      title: 'Property Sales',
      description: 'Expert guidance through the entire property selling process with maximum value realization.',
      features: [
        'Professional property valuation',
        'Strategic marketing campaigns',
        'Negotiation expertise',
        'Legal documentation support',
        'Market analysis and pricing strategy'
      ]
    },
    {
      id: 'buying',
      icon: Search,
      title: 'Property Buying Assistance',
      description: 'Comprehensive support to help you find and secure your perfect property investment.',
      features: [
        'Personalized property search',
        'Area market insights',
        'Property inspection coordination',
        'Bond application assistance',
        'Transfer process management'
      ]
    },
    {
      id: 'management',
      icon: Users,
      title: 'Property Management',
      description: 'Complete property management services for landlords and property investors.',
      features: [
        'Tenant screening and placement',
        'Rent collection and accounting',
        'Maintenance coordination',
        'Regular property inspections',
        'Legal compliance management'
      ]
    },
    {
      id: 'valuation',
      icon: TrendingUp,
      title: 'Property Valuation',
      description: 'Professional property valuations for sales, purchases, insurance, and investment decisions.',
      features: [
        'Certified property valuations',
        'Market comparison analysis',
        'Investment potential assessment',
        'Insurance valuation reports',
        'Bank valuation services'
      ]
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'We understand your needs and provide expert advice tailored to your situation.'
    },
    {
      step: '02',
      title: 'Strategy Development',
      description: 'Create a customized plan based on market analysis and your specific goals.'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'Execute the strategy with regular updates and transparent communication.'
    },
    {
      step: '04',
      title: 'Successful Completion',
      description: 'Achieve your property goals with ongoing support and post-transaction services.'
    }
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(88,101,242,0.08),transparent_50%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-8"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm">Comprehensive Real Estate Solutions</span>
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              Our Services
            </h1>
            <p className="text-xl text-muted-foreground mb-10">
              Comprehensive property services designed to meet all your real estate needs 
              with expert guidance, transparent processes, and proven results.
            </p>
            <Button size="lg" onClick={onNavigateToProperties} className="px-8 py-6 text-lg bg-primary hover:bg-primary/90 group" data-testid="modern-services-hero-browse-properties-button">
              <Search className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
              Browse Properties
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Core Offerings Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">What We Offer</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Specialized services to support every aspect of your property journey
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreOfferings.map((offering, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group h-full">
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${offering.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <offering.icon className="w-7 h-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl mb-2">{offering.title}</CardTitle>
                    <CardDescription className="text-base leading-relaxed">
                      {offering.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 relative bg-muted/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Core Services</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Our comprehensive range of property services covers every aspect of your real estate journey
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group h-full">
                  <CardHeader>
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <service.icon className="w-7 h-7 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                        <CardDescription className="text-base">{service.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Process</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              A streamlined approach to achieving your property goals
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 relative overflow-hidden group h-full">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent opacity-50 rounded-bl-full" />
                  <CardHeader>
                    <div className="text-5xl font-bold text-primary/20 mb-4">{step.step}</div>
                    <CardTitle className="text-xl mb-2">{step.title}</CardTitle>
                    <CardDescription className="text-base leading-relaxed">
                      {step.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-muted-foreground mb-10">
              Let our expert team guide you through your property journey
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={onNavigateToProperties}
                className="px-10 py-7 text-lg bg-primary hover:bg-primary/90 group"
                data-testid="modern-services-cta-browse-properties-button"
              >
                Browse Properties
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={onNavigateToHub}
                className="px-10 py-7 text-lg border-border/50 hover:border-primary/50 hover:bg-primary/5"
                data-testid="modern-services-contact-us-button"
              >
                Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}